import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import {
  IconButton, Tooltip, Dialog, DialogTitle, DialogContent,
  DialogContentText, DialogActions, Snackbar, Alert, Popover,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import DoNotDisturbIcon from "@mui/icons-material/DoNotDisturb";
import BlockIcon from "@mui/icons-material/Block";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { pageStyles } from "@/components/utils/PageLayout";
import { PageFrame, PageHeader, Surface, Button as KitButton, Note, Placeholder, Icon } from "@/components/ui";
import { orderExecutionApi } from "@/services/api";
import { formatNumber, formatCurrency } from "@/utils/formatters";
import { useColumnOrder } from "@/hooks/useColumnOrder";
import { useVisiblePolling } from "@/hooks/useVisiblePolling";
import { DEFAULT_SCHEME_ORDER } from "./config";
import { buildExecutionTableHTML } from "./buildExecutionTableHTML";
import type {
  ExecutionMatrixResponse,
  ExecutionMatrixRow,
  SchemeExecutionCell,
} from "@/types/client";
import s from "./OrderExecutionDashboard.module.css";
import shared from '@/components/utils/shared.module.css';


const CMP_POLL_INTERVAL = 15000;

const STATUS_OPTIONS: { label: string; value: string; code?: number; tooltip: string }[] = [
  { label: "All", value: "ALL", tooltip: "Show all executions" },
  { label: "Open", value: "OPEN", code: 1, tooltip: "Order placed with the broker — no qty filled yet" },
  { label: "Partial", value: "PARTIALLY_FILLED", code: 2, tooltip: "Some qty has been executed; remaining fill is pending" },
  { label: "Completed", value: "FILLED", code: 3, tooltip: "All ordered qty has been fully executed" },
  { label: "Cancelled", value: "CANCELLED", code: 4, tooltip: "Execution was cancelled; no qty was filled" },
  { label: "Closed", value: "CLOSED", code: 5, tooltip: "Manually closed with partial fill; remaining qty was abandoned" },
];

const SCHEME_STATUS_CLASS: Record<number, string> = {
  1: s["scheme-open"], 2: s["scheme-partial"], 3: s["scheme-completed"],
  4: s["scheme-cancelled"], 5: s["scheme-closed"],
};

const ORDER_SIDE_BUY = 1 as const;
const ORDER_SIDE_SELL = 2 as const;

const NRI_PMS_SCHEME_CODE = "IEF";

const STATUS_OPEN = 1;
const STATUS_PARTIAL = 2;
const STATUS_FILLED = 3;
const STATUS_CANCELLED = 4;
const STATUS_CLOSED = 5;
const ACTIVE_STATUSES = [STATUS_OPEN, STATUS_PARTIAL];
const DONE_STATUSES = [STATUS_FILLED, STATUS_CANCELLED, STATUS_CLOSED];

type SortField = "company" | "limit_price" | "cmp" | "slippage";

interface TableState {
  filter: string;
  search: string;
  sortField: SortField;
  sortDir: "asc" | "desc";
}
const INITIAL_TABLE_STATE: TableState = { filter: "ALL", search: "", sortField: "company", sortDir: "asc" };

interface EditState {
  executionId: number;
  field: "executed_qty" | "limit_price";
  value: string;
  orderSide: 1 | 2;
}

interface ConfirmState {
  open: boolean;
  type: "close" | "cancel" | null;
  executionId: number | null;
  orderSide: 1 | 2 | null;
  companyName: string;
  schemeName: string;
  orderedQty: number;
  executedQty: number;
  statusDisplay: string;
}

const EMPTY_CONFIRM: ConfirmState = {
  open: false, type: null, executionId: null, orderSide: null,
  companyName: "", schemeName: "", orderedQty: 0, executedQty: 0, statusDisplay: "",
};

// --- Memoized scheme cell (avoids re-rendering all cells during CMP polling) ---
interface SchemeCellProps {
  cell: SchemeExecutionCell;
  field: "ordered_qty" | "executed_qty";
  companyName: string;
  schemeCode: string;
  orderSide: 1 | 2;
  isEditing: boolean;
  editValue: string;
  saving: boolean;
  isDimmed?: boolean;
  onEdit: (executionId: number, field: "executed_qty" | "limit_price", currentValue: number, orderSide: 1 | 2) => void;
  onEditChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSave: () => void;
  onCancelEdit: () => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
  onConfirm: (type: "close" | "cancel", cell: SchemeExecutionCell, companyName: string, orderSide: 1 | 2) => void;
  onPopover: (anchor: HTMLElement, data: NonNullable<SchemeExecutionCell["source_order"]>, orderSide: 1 | 2) => void;
}

/* eslint-disable react/prop-types */
const SchemeCell = React.memo<SchemeCellProps>(({
  cell, field, companyName, schemeCode, orderSide,
  isEditing, editValue, saving, isDimmed,
  onEdit, onEditChange, onSave, onCancelEdit, onKeyDown,
  onConfirm, onPopover,
}) => {
  const cellBg = SCHEME_STATUS_CLASS[cell.execution_status] || "";
  const value = field === "ordered_qty" ? cell.ordered_qty : cell.executed_qty;
  const cellActive = ACTIVE_STATUSES.includes(cell.execution_status);
  const isDone = DONE_STATUSES.includes(cell.execution_status);
  const isExecutedField = field === "executed_qty";

  if (isEditing) {
    return (
      <td className={`${s["scheme-cell"]} ${cellBg}`}>
        <span className={s["edit-cell"]}>
          <input className={s["edit-input"]} value={editValue}
            onChange={onEditChange} onKeyDown={onKeyDown} autoFocus />
          <Tooltip title="Save">
            <IconButton size="small" color="primary" onClick={onSave} disabled={saving} sx={{ padding: "2px" }}>
              <SaveIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Cancel">
            <IconButton size="small" color="default" onClick={onCancelEdit} sx={{ padding: "2px" }}>
              <CancelIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </span>
      </td>
    );
  }

  return (
    <td className={`${s["scheme-cell"]} ${cellBg}`}>
      <span className={isDone || isDimmed ? s.dimmed : ""}>
        {formatNumber(value, 0)}
        {isExecutedField && cell.executed_qty > 0 && cell.ordered_qty > 0 && (
          <span className={s["fill-pct"]}> ({Math.round((cell.executed_qty / cell.ordered_qty) * 100)}%)</span>
        )}
      </span>
      {!isExecutedField && !cell.is_pov_excluded && cell.pov_percentage != null && (
        <Tooltip title="Allocated POV" placement="bottom" arrow>
          <div className={s["pov-scheme-value"]}>
            {cell.pov_percentage.toFixed(1)}%
          </div>
        </Tooltip>
      )}
      {!isExecutedField && cell.source_order && (
        <div className={s["cell-overlay"]}>
          <Tooltip title="Order Details">
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); onPopover(e.currentTarget, cell.source_order!, orderSide); }}
              sx={{ padding: "2px", color: "var(--info)" }}
            >
              <InfoOutlinedIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
        </div>
      )}
      {isExecutedField && cellActive && (
        <div className={s["cell-overlay"]}>
          <Tooltip title="Edit">
            <IconButton size="small" onClick={() => onEdit(cell.id, "executed_qty", cell.executed_qty, orderSide)} sx={{ padding: "2px" }}>
              <EditIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Close">
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); onConfirm("close", cell, companyName, orderSide); }} sx={{ padding: "2px", color: "var(--text-dim)" }}>
              <DoNotDisturbIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Cancel">
            <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); onConfirm("cancel", cell, companyName, orderSide); }} sx={{ padding: "2px" }}>
              <BlockIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
        </div>
      )}
    </td>
  );
}, (prev, next) =>
  prev.cell.id === next.cell.id
  && prev.cell.ordered_qty === next.cell.ordered_qty
  && prev.cell.executed_qty === next.cell.executed_qty
  && prev.cell.execution_status === next.cell.execution_status
  && prev.cell.pov_percentage === next.cell.pov_percentage
  && prev.cell.is_pov_excluded === next.cell.is_pov_excluded
  && prev.field === next.field
  && prev.isEditing === next.isEditing
  && prev.editValue === next.editValue
  && prev.saving === next.saving
  && prev.isDimmed === next.isDimmed
);
/* eslint-enable react/prop-types */
SchemeCell.displayName = "SchemeCell";

const OrderExecutionDashboard: React.FC = () => {
  const [buyData, setBuyData] = useState<ExecutionMatrixResponse | null>(null);
  const [sellData, setSellData] = useState<ExecutionMatrixResponse | null>(null);
  const [cmpData, setCmpData] = useState<Record<number, number | null>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editState, setEditState] = useState<EditState | null>(null);
  const [saving, setSaving] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<ConfirmState>(EMPTY_CONFIRM);
  const [buyState, setBuyState] = useState<TableState>(INITIAL_TABLE_STATE);
  const [sellState, setSellState] = useState<TableState>(INITIAL_TABLE_STATE);
  const updateTableState = (side: 1 | 2, patch: Partial<TableState>) =>
    (side === ORDER_SIDE_BUY ? setBuyState : setSellState)((prev) => ({ ...prev, ...patch }));
  const [flashIds, setFlashIds] = useState<Set<number>>(new Set());
  const prevCmpRef = useRef<Record<number, number | null>>({});
  const dataRef = useRef({ buyData, sellData, cmpData });
  dataRef.current = { buyData, sellData, cmpData };
  const [povEditState, setPovEditState] = useState<{ companyId: number; orderSide: 1 | 2; value: string } | null>(null);
  const [orderPopover, setOrderPopover] = useState<{
    anchor: HTMLElement;
    data: NonNullable<SchemeExecutionCell["source_order"]>;
    orderSide: 1 | 2;
  } | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: "success" | "error" }>({
    open: false, message: "", severity: "success",
  });

  const instanceId = useId();
  const defaults = buyData?.defaults ?? sellData?.defaults;

  // --- Scheme column ordering (persisted in sessionStorage) ---
  const allServerSchemes = useMemo(() => {
    const set = new Set<string>();
    buyData?.schemes.forEach((s) => set.add(s));
    sellData?.schemes.forEach((s) => set.add(s));
    return Array.from(set).sort();
  }, [buyData, sellData]);

  const { columns: orderedSchemes, sync: syncSchemes, onDragStart, onDragOver, onDrop } = useColumnOrder("execution_schemes", allServerSchemes, DEFAULT_SCHEME_ORDER);

  // Sync when server data changes (new schemes appear / old ones removed)
  useEffect(() => {
    if (allServerSchemes.length > 0) syncSchemes(allServerSchemes);
  }, [allServerSchemes]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Fetch matrix data ---
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
        const [buyRes, sellRes] = await Promise.all([
          orderExecutionApi.getMatrix({ order_side: ORDER_SIDE_BUY }),
          orderExecutionApi.getMatrix({ order_side: ORDER_SIDE_SELL }),
        ]);
        setBuyData(buyRes);
        setSellData(sellRes);
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to load execution data");
    } finally {
      setLoading(false);
    }
  }, []);

  /** Refresh only one side of the matrix after a targeted mutation (POV save, qty edit, close/cancel). */
  const fetchSide = useCallback(async (orderSide: 1 | 2) => {
    try {
      const res = await orderExecutionApi.getMatrix({ order_side: orderSide });
      if (orderSide === ORDER_SIDE_BUY) setBuyData(res);
      else setSellData(res);
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to refresh execution data");
    }
  }, []);

  // --- Poll CMP every 5s ---
  const fetchCmp = useCallback(async () => {
    const { buyData: buy, sellData: sell, cmpData: currentCmp } = dataRef.current;
    const allIds = new Set<number>();
    [buy, sell].forEach((data) => {
      data?.results.forEach((r) => allIds.add(r.company.id));
    });
    if (allIds.size === 0) return;
    try {
      const raw = await orderExecutionApi.getBulkCmp(Array.from(allIds));
      const parsed: Record<number, number | null> = {};
      const changed = new Set<number>();
      for (const [id, data] of Object.entries(raw)) {
        const numId = Number(id);
        const newVal = data?.ltp ?? null;
        parsed[numId] = newVal;
        if (prevCmpRef.current[numId] !== undefined && prevCmpRef.current[numId] !== newVal) {
          changed.add(numId);
        }
      }
      prevCmpRef.current = parsed;
      if (changed.size > 0) {
        setCmpData(parsed);
        setFlashIds(changed);
        setTimeout(() => setFlashIds(new Set()), 700);
      } else if (Object.keys(parsed).length !== Object.keys(currentCmp).length) {
        setCmpData(parsed);
      }
    } catch (err) { console.warn("CMP poll failed (best-effort):", err); }
  }, []); // stable — reads from dataRef

  useEffect(() => { fetchData(); }, [fetchData]);

  // Poll CMP on a fixed interval (starts on mount), paused while the tab is hidden
  useVisiblePolling(fetchCmp, CMP_POLL_INTERVAL);

  // Trigger first CMP fetch immediately when matrix data arrives
  useEffect(() => {
    if (buyData || sellData) fetchCmp();
  }, [Boolean(buyData), Boolean(sellData)]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Sort (per side) ---
  const handleSort = (field: SortField, orderSide: 1 | 2) => {
    const ts = orderSide === ORDER_SIDE_BUY ? buyState : sellState;
    if (ts.sortField === field) updateTableState(orderSide, { sortDir: ts.sortDir === "asc" ? "desc" : "asc" });
    else updateTableState(orderSide, { sortField: field, sortDir: "asc" });
  };
  const sortIcon = (field: SortField, orderSide: 1 | 2) => {
    const ts = orderSide === ORDER_SIDE_BUY ? buyState : sellState;
    if (ts.sortField !== field) return <span className={pageStyles["sort-icon"]} style={{ opacity: 0.3 }}>↕</span>;
    return <span className={pageStyles["sort-icon"]}>{ts.sortDir === "asc" ? "↑" : "↓"}</span>;
  };
  const thClass = (field: SortField, orderSide: 1 | 2) => {
    const ts = orderSide === ORDER_SIDE_BUY ? buyState : sellState;
    return `${pageStyles["th-sortable"]} ${ts.sortField === field ? pageStyles["th-active"] : ""}`;
  };

  // --- Edit ---
  const toEditDisplay = (field: "executed_qty" | "limit_price", value: number): string => {
    if (field === "executed_qty") return formatNumber(value, 0);
    return String(value);
  };

  const handleEditChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!editState) return;
    const raw = e.target.value.replace(/,/g, "");
    if (editState.field === "executed_qty") {
      const digits = raw.replace(/\D/g, "");
      setEditState({ ...editState, value: digits ? formatNumber(Number(digits), 0) : "" });
    } else {
      setEditState({ ...editState, value: raw.replace(/[^0-9.]/g, "") });
    }
  };

  const handleEdit = (executionId: number, field: "executed_qty" | "limit_price", currentValue: number, orderSide: 1 | 2) => {
    setEditState({ executionId, field, value: toEditDisplay(field, currentValue), orderSide });
  };
  const handleSave = async () => {
    if (!editState) return;
    setSaving(true);
    try {
      const raw = editState.value.replace(/,/g, "");
      const payload: Record<string, any> = {};
      if (editState.field === "executed_qty") payload.executed_qty = parseInt(raw, 10);
      else payload.limit_price = raw;
      const { orderSide } = editState;
      await orderExecutionApi.update(editState.executionId, payload);
      setEditState(null);
      fetchSide(orderSide);
    } catch (err: any) { setSnackbar({ open: true, message: err?.response?.data?.detail || "Update failed", severity: "error" }); }
    finally { setSaving(false); }
  };
  const handleCancelEdit = () => setEditState(null);
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") handleSave();
    if (e.key === "Escape") handleCancelEdit();
  };

  // --- Shared inline edit input ---
  const renderInlineEdit = (opts: {
    value: string;
    inputClassName: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onKeyDown: (e: React.KeyboardEvent) => void;
    onSave: () => void;
    onCancel: () => void;
    disabled?: boolean;
    inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
  }) => (
    <span className={s["edit-cell"]}>
      <input className={opts.inputClassName} value={opts.value}
        onChange={opts.onChange} onKeyDown={opts.onKeyDown} autoFocus {...opts.inputProps} />
      <Tooltip title="Save">
        <IconButton size="small" color="primary" onClick={opts.onSave} disabled={opts.disabled} sx={{ padding: "2px" }}>
          <SaveIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
      <Tooltip title="Cancel">
        <IconButton size="small" color="default" onClick={opts.onCancel} sx={{ padding: "2px" }}>
          <CancelIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
    </span>
  );

  // --- Close / Cancel with confirmation ---
  const confirmAction = (
    type: "close" | "cancel",
    cell: SchemeExecutionCell,
    companyName: string,
    orderSide: 1 | 2,
  ) => {
    setConfirmDialog({
      open: true, type, executionId: cell.id, orderSide,
      companyName,
      schemeName: cell.scheme_name,
      orderedQty: cell.ordered_qty,
      executedQty: cell.executed_qty,
      statusDisplay: cell.execution_status_display,
    });
  };

  const executeConfirmedAction = async () => {
    const { type, executionId, orderSide } = confirmDialog;
    setConfirmDialog(EMPTY_CONFIRM);
    if (!executionId || !orderSide) return;
    try {
      if (type === "close") await orderExecutionApi.close(executionId);
      else await orderExecutionApi.cancel(executionId);
      fetchSide(orderSide);
    } catch (err: any) {
      const fallback = type === "close" ? "Failed to close execution" : "Failed to cancel execution";
      setSnackbar({ open: true, message: err?.response?.data?.detail || fallback, severity: "error" });
    }
  };

  const handleShowPopover = useCallback((anchor: HTMLElement, data: NonNullable<SchemeExecutionCell["source_order"]>, orderSide: 1 | 2) => {
    setOrderPopover({ anchor, data, orderSide });
  }, []);

  // --- POV edit handlers ---
  const handlePovEdit = (companyId: number, orderSide: 1 | 2, currentValue: number) => {
    setPovEditState({ companyId, orderSide, value: String(currentValue) });
  };
  const handlePovSave = async () => {
    if (!povEditState) return;
    const numVal = parseFloat(povEditState.value);
    const minPov = defaults?.min_pov ?? 1;
    const maxPov = defaults?.max_pov ?? 50;
    if (isNaN(numVal) || numVal < minPov || numVal > maxPov) {
      setPovEditState(null);
      setSnackbar({ open: true, message: `Total POV must be between ${minPov}% and ${maxPov}%.`, severity: "error" });
      return;
    }
    try {
      await orderExecutionApi.updateTotalPov({
        company_id: povEditState.companyId,
        order_side: povEditState.orderSide,
        total_pov_percentage: numVal,
      });
      const side = povEditState.orderSide;
      setPovEditState(null);
      setSnackbar({ open: true, message: `Total POV updated to ${numVal}%.`, severity: "success" });
      fetchSide(side);
    } catch (err: any) {
      setPovEditState(null);
      const msg = err?.response?.data?.detail || "Failed to update total POV";
      setSnackbar({ open: true, message: msg, severity: "error" });
    }
  };
  const handlePovKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") handlePovSave();
    if (e.key === "Escape") setPovEditState(null);
  };

  // --- Difference helpers ---
  // Buy: limit - cmp (positive = we're paying above market)
  // Sell: cmp - limit (positive = market is above our ask)
  const getDifference = useCallback((companyId: number, limitPrice: number, orderSide: 1 | 2): { value: number; pct: number } | null => {
    const cmp = cmpData[companyId];
    if (cmp == null || !limitPrice) return null;
    const value = orderSide === ORDER_SIDE_BUY ? limitPrice - cmp : cmp - limitPrice;
    const pct = (value / limitPrice) * 100;
    return { value, pct };
  }, [cmpData]);

  const getDifferenceClass = (diff: { value: number } | null): string => {
    if (!diff || diff.value === 0) return "";
    // Positive = favorable (green), Negative = unfavorable (red)
    return diff.value > 0 ? s["diff-positive"] : s["diff-negative"];
  };

  const filterAndSort = useCallback((
    results: ExecutionMatrixRow[], orderSide: 1 | 2, ts: TableState,
  ): ExecutionMatrixRow[] => {
    let filtered = results;
    if (ts.search.trim()) {
      const q = ts.search.trim().toLowerCase();
      filtered = filtered.filter((row) =>
        row.company.symbol.toLowerCase().includes(q) || row.company.name.toLowerCase().includes(q)
      );
    }
    if (ts.filter !== "ALL") {
      const statusCode = STATUS_OPTIONS.find((o) => o.value === ts.filter)?.code;
      if (statusCode) {
        filtered = filtered
          .map((row) => {
            const matchedSchemes: Record<string, SchemeExecutionCell | null> = {};
            let hasMatch = false;
            for (const [code, cell] of Object.entries(row.schemes)) {
              if (cell && cell.execution_status === statusCode) {
                matchedSchemes[code] = cell;
                hasMatch = true;
              } else {
                matchedSchemes[code] = null;
              }
            }
            return hasMatch ? { ...row, schemes: matchedSchemes } : null;
          })
          .filter((r): r is ExecutionMatrixRow => r !== null);
      }
    }
    return [...filtered].sort((a, b) => {
      let cmp = 0;
      switch (ts.sortField) {
        case "company": cmp = a.company.symbol.localeCompare(b.company.symbol); break;
        case "limit_price": cmp = a.limit_price - b.limit_price; break;
        case "cmp": cmp = (cmpData[a.company.id] ?? 0) - (cmpData[b.company.id] ?? 0); break;
        case "slippage": {
          const sa = getDifference(a.company.id, a.limit_price, orderSide);
          const sb = getDifference(b.company.id, b.limit_price, orderSide);
          cmp = (sa?.value ?? 0) - (sb?.value ?? 0);
          break;
        }
      }
      return ts.sortDir === "asc" ? cmp : -cmp;
    });
  }, [cmpData, getDifference]);

  const buyResults = useMemo(() => buyData ? filterAndSort(buyData.results, ORDER_SIDE_BUY, buyState) : [], [buyData, buyState, filterAndSort]);
  const sellResults = useMemo(() => sellData ? filterAndSort(sellData.results, ORDER_SIDE_SELL, sellState) : [], [sellData, sellState, filterAndSort]);

  // Memoize datalist suggestions so they don't re-compute on every CMP poll / flash cycle.
  const buySuggestions = useMemo(() =>
    buyData?.results.map((row) => ({ id: row.company.id, symbol: row.company.symbol, name: row.company.name })) ?? [],
    [buyData],
  );
  const sellSuggestions = useMemo(() =>
    sellData?.results.map((row) => ({ id: row.company.id, symbol: row.company.symbol, name: row.company.name })) ?? [],
    [sellData],
  );

  const handleCopyToClipboard = async () => {
    const allResults = [...(buyData?.results || []), ...(sellData?.results || [])];
    if (allResults.length === 0) {
      setSnackbar({ open: true, message: "No data to copy", severity: "error" });
      return;
    }

    try {
      const html = buildExecutionTableHTML({ buyResults, sellResults, schemes: orderedSchemes, cmpData, getDifference });

      // execCommand lets the browser write CF_HTML format, which Excel requires.
      // navigator.clipboard.write with text/html writes raw bytes that Excel ignores.
      const el = document.createElement("div");
      el.setAttribute("contenteditable", "true");
      el.style.cssText = "position:fixed;top:-9999px;left:-9999px;opacity:0;pointer-events:none;";
      el.innerHTML = html;
      document.body.appendChild(el);

      let success = false;
      try {
        const range = document.createRange();
        range.selectNodeContents(el);
        const selection = window.getSelection();
        selection?.removeAllRanges();
        selection?.addRange(range);
        success = document.execCommand("copy");
        selection?.removeAllRanges();
      } finally {
        document.body.removeChild(el);
      }

      if (!success) throw new Error("execCommand copy returned false");
      setSnackbar({ open: true, message: "Table copied — paste into Excel or Sheets", severity: "success" });
    } catch (err) {
      console.error(err);
      setSnackbar({ open: true, message: "Failed to copy table", severity: "error" });
    }
  };

  // --- Render table ---
  const renderTable = (config: {
    title: string;
    data: ExecutionMatrixResponse | null;
    orderSide: 1 | 2;
    results: ExecutionMatrixRow[];
    suggestions: { id: number; symbol: string; name: string }[];
  }) => {
    const { title, data, orderSide, results, suggestions } = config;
    const ts = orderSide === ORDER_SIDE_BUY ? buyState : sellState;
    const { filter, search } = ts;
    const setFilter = (v: string) => updateTableState(orderSide, { filter: v });
    const setSearch = (v: string) => updateTableState(orderSide, { search: v });
    if (!data) return null;
    const schemes = orderedSchemes.filter((sc) => data.schemes.includes(sc));

    const pills = (
      <div className={s["status-pills"]}>
        {STATUS_OPTIONS.map((opt) => (
          <Tooltip key={opt.value} title={opt.tooltip} placement="top" arrow>
            <KitButton
              size="sm"
              variant={filter === opt.value ? "primary" : "secondary"}
              aria-pressed={filter === opt.value}
              onClick={() => setFilter(opt.value)}
            >
              {opt.label}
            </KitButton>
          </Tooltip>
        ))}
        <div className={s["search-box"]}>
          <input
            type="text"
            placeholder="Search company..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            list={`${instanceId}-suggest-${orderSide}`}
          />
          {search && (
            <KitButton
              iconOnly
              className={s["search-clear"]}
              onClick={() => setSearch("")}
              title="Clear search"
              aria-label="Clear search"
            >
              <Icon name="close" size={14} />
            </KitButton>
          )}
          <datalist id={`${instanceId}-suggest-${orderSide}`}>
            {suggestions.map((c) => (
              <option key={c.id} value={c.symbol}>
                {c.name}
              </option>
            ))}
          </datalist>
        </div>
      </div>
    );

    if (results.length === 0) {
      return (
        <Surface title={title}>
          <div className={s.section}>
            {pills}
            <Placeholder message="No executions found" />
          </div>
        </Surface>
      );
    }

    return (
      <Surface title={title}>
      <div className={s.section}>
        {pills}
        <div className={s["status-legend"]}>
          {STATUS_OPTIONS.filter((o) => o.code).map((opt) => (
            <span key={opt.value} className={s["legend-item"]}>
              <span className={`${s["legend-swatch"]} ${SCHEME_STATUS_CLASS[opt.code as number]}`} />
              {opt.label}
            </span>
          ))}
        </div>
        <div className={pageStyles["table-wrapper"]}>
          <div className={pageStyles["table-scroll"]}>
            <table className={`${pageStyles.table} ${s["bordered-table"]}`}>
              <thead>
                <tr>
                  <th rowSpan={2} className={`${thClass("company", orderSide)} ${s["sticky-company"]}`} onClick={() => handleSort("company", orderSide)}>
                    Company {sortIcon("company", orderSide)}
                  </th>
                  <th rowSpan={2} className={`${pageStyles["text-right"]} ${thClass("limit_price", orderSide)} ${s["sticky-limit-price"]}`} onClick={() => handleSort("limit_price", orderSide)}>
                    Limit Price {sortIcon("limit_price", orderSide)}
                  </th>
                  <th rowSpan={2} className={`${pageStyles["text-right"]} ${thClass("cmp", orderSide)}`} onClick={() => handleSort("cmp", orderSide)}>
                    CMP {sortIcon("cmp", orderSide)}
                  </th>
                  <th rowSpan={2} className={`${pageStyles["text-right"]} ${thClass("slippage", orderSide)}`} onClick={() => handleSort("slippage", orderSide)}>
                    <Tooltip title={orderSide === ORDER_SIDE_BUY ? "Limit − CMP" : "CMP − Limit"} placement="top">
                      <span style={{ cursor: "help", borderBottom: "1px dashed var(--text-faint)" }}>Difference</span>
                    </Tooltip>
                    {" "}{sortIcon("slippage", orderSide)}
                  </th>
                  <th rowSpan={2} className={pageStyles["text-right"]}>
                    <Tooltip title="Total POV %" placement="top">
                      <span style={{ cursor: "help", borderBottom: "1px dashed var(--text-faint)" }}>POV %</span>
                    </Tooltip>
                  </th>
                  {schemes.map((scheme) => {
                    const globalIdx = orderedSchemes.indexOf(scheme);
                    return (
                      <th
                        key={scheme}
                        colSpan={2}
                        className={s["scheme-header"]}
                        draggable
                        onDragStart={() => onDragStart(globalIdx)}
                        onDragOver={onDragOver}
                        onDrop={() => onDrop(globalIdx)}
                      >
                        <span className={s["scheme-header-inner"]}>
                          <DragIndicatorIcon sx={{ fontSize: 14, opacity: 0.4, cursor: "grab" }} />
                          <span>{scheme}</span>
                        </span>
                      </th>
                    );
                  })}
                </tr>
                <tr>
                  {schemes.map((scheme) => (
                    <React.Fragment key={`${scheme}-sub`}>
                      <th className={`${s["sub-header"]} ${s["scheme-border-left"]}`}>Order Qty</th>
                      <th className={s["sub-header"]}>Exec Qty</th>
                    </React.Fragment>
                  ))}
                </tr>
              </thead>
              <tbody>
                {results.map((row) => {
                  const rowKey = `${orderSide}-${row.company.id}`;
                  const activeExecId = (Object.values(row.schemes).find(
                    (c): c is SchemeExecutionCell => c !== null && ACTIVE_STATUSES.includes((c as SchemeExecutionCell).execution_status)
                  ) as SchemeExecutionCell | undefined)?.id ?? null;
                  const cmp = cmpData[row.company.id];
                  const diff = getDifference(row.company.id, row.limit_price, orderSide);
                  const isPovEditing = povEditState?.companyId === row.company.id && povEditState?.orderSide === orderSide;

                  // Grey out the NRI PMS scheme (NRI_PMS_SCHEME_CODE) only if another scheme on the
                  // same symbol still has a live order
                  const hasIEF = row.schemes[NRI_PMS_SCHEME_CODE] != null;
                  const shouldDimIEF = hasIEF && Object.entries(row.schemes).some(
                    ([code, c]) => code !== NRI_PMS_SCHEME_CODE && c != null && ACTIVE_STATUSES.includes((c as SchemeExecutionCell).execution_status)
                  );

                  return (
                    <React.Fragment key={rowKey}>
                      <tr>
                        <td className={s["sticky-company"]}>
                          <span className={s["company-cell"]}>
                            <span title={row.company.name}>{row.company.symbol}</span>
                          </span>
                        </td>
                        <td className={`${pageStyles["text-right"]} ${s["scheme-cell"]} ${s["sticky-limit-price"]}`}>
                          {editState?.executionId === (activeExecId ?? -1) && editState?.field === "limit_price" ? (
                            renderInlineEdit({
                              value: editState!.value,
                              inputClassName: s["edit-input"],
                              onChange: handleEditChange,
                              onKeyDown: handleKeyDown,
                              onSave: handleSave,
                              onCancel: handleCancelEdit,
                              disabled: saving,
                            })
                          ) : (
                            <>
                              {formatCurrency(row.limit_price)}
                              {activeExecId && (
                                <div className={s["cell-overlay"]}>
                                  <Tooltip title="Edit Limit Price">
                                    <IconButton size="small" onClick={() => handleEdit(activeExecId!, "limit_price", row.limit_price, orderSide)} sx={{ padding: "2px" }}>
                                      <EditIcon sx={{ fontSize: 15 }} />
                                    </IconButton>
                                  </Tooltip>
                                </div>
                              )}
                            </>
                          )}
                        </td>
                        <td className={`${pageStyles["text-right"]} ${flashIds.has(row.company.id) ? s["cmp-flash"] : ""}`}>
                          {cmp != null ? formatCurrency(cmp) : ""}
                        </td>
                        <td className={`${pageStyles["text-right"]} ${getDifferenceClass(diff)}`}>
                          {diff ? (
                            <>
                              {diff.value >= 0 ? "+" : ""}{formatCurrency(diff.value)}
                              {" "}
                              <span className={s["slippage-pct"]}>({diff.value >= 0 ? "+" : ""}{diff.pct.toFixed(2)}%)</span>
                            </>
                          ) : ""}
                        </td>
                        <td className={`${pageStyles["text-right"]} ${s["pov-cell"]}`}>
                          {isPovEditing ? (
                            renderInlineEdit({
                              value: povEditState!.value,
                              inputClassName: s["pov-edit-input"],
                              onChange: (e) => {
                                const v = e.target.value;
                                if (v === "" || /^\d*\.?\d?$/.test(v)) setPovEditState({ ...povEditState!, value: v });
                              },
                              onKeyDown: handlePovKeyDown,
                              onSave: handlePovSave,
                              onCancel: () => setPovEditState(null),
                              inputProps: { type: "text", inputMode: "decimal" },
                            })
                          ) : row.total_pov_percentage == null ? null : (
                            <>
                              {row.pov_error ? (
                                <span className={s["pov-error-row"]}>
                                  <Tooltip title={row.pov_error} placement="bottom" arrow>
                                    <span className={s["pov-error"]}>!</span>
                                  </Tooltip>
                                  <Tooltip title="Edit Total POV">
                                    <IconButton size="small" onClick={() => handlePovEdit(row.company.id, orderSide, row.total_pov_percentage!)} sx={{ padding: "2px" }}>
                                      <EditIcon sx={{ fontSize: 14 }} />
                                    </IconButton>
                                  </Tooltip>
                                </span>
                              ) : (
                                <>
                                  {row.total_pov_percentage}%
                                  <div className={s["cell-overlay"]}>
                                    <Tooltip title="Edit Total POV">
                                      <IconButton size="small" onClick={() => handlePovEdit(row.company.id, orderSide, row.total_pov_percentage!)} sx={{ padding: "2px" }}>
                                        <EditIcon sx={{ fontSize: 15 }} />
                                      </IconButton>
                                    </Tooltip>
                                  </div>
                                </>
                              )}
                            </>
                          )}
                        </td>
                        {schemes.map((schemeCode) => {
                          const cell = row.schemes[schemeCode] as SchemeExecutionCell | null;
                          if (!cell) {
                            return (
                              <React.Fragment key={schemeCode}>
                                <td className={s["scheme-border-left"]}></td>
                                <td></td>
                              </React.Fragment>
                            );
                          }
                          const isDimmed = schemeCode === NRI_PMS_SCHEME_CODE && shouldDimIEF;
                          return (
                            <React.Fragment key={schemeCode}>
                              <SchemeCell
                                cell={cell} field="ordered_qty" companyName={row.company.name}
                                schemeCode={schemeCode} orderSide={orderSide}
                                isEditing={false}
                                editValue="" saving={false}
                                isDimmed={isDimmed}
                                onEdit={handleEdit} onEditChange={handleEditChange}
                                onSave={handleSave} onCancelEdit={handleCancelEdit}
                                onKeyDown={handleKeyDown} onConfirm={confirmAction}
                                onPopover={handleShowPopover}
                              />
                              <SchemeCell
                                cell={cell} field="executed_qty" companyName={row.company.name}
                                schemeCode={schemeCode} orderSide={orderSide}
                                isEditing={editState?.executionId === cell.id && editState?.field === "executed_qty"}
                                editValue={editState?.value ?? ""} saving={saving}
                                isDimmed={isDimmed}
                                onEdit={handleEdit} onEditChange={handleEditChange}
                                onSave={handleSave} onCancelEdit={handleCancelEdit}
                                onKeyDown={handleKeyDown} onConfirm={confirmAction}
                                onPopover={handleShowPopover}
                              />
                            </React.Fragment>
                          );
                        })}
                      </tr>
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        <div className={s["disclaimer"]}><strong>Note:</strong> Only schemes with active orders are shown as columns. Drag column headers to reorder.</div>
      </div>
      </Surface>
    );
  };

  const headerActions = (
    <div className={pageStyles["header-actions"]}>
      {defaults && (
        <div className={s["pov-defaults"]}>
          <span><strong>Default Total POV:</strong> {defaults.total_pov}%</span>
          <span><strong>Default Min POV:</strong> {defaults.min_pov}%</span>
        </div>
      )}
      <KitButton onClick={handleCopyToClipboard} icon={<ContentCopyIcon fontSize="small" />}>
        Copy Table
      </KitButton>
    </div>
  );

  return (
    <PageFrame
      header={
        <PageHeader
          title="Order Execution"
          subtitle="Live fills against each open order, by scheme."
          actions={headerActions}
        />
      }
    >
      {loading ? (
        <Surface>
          <Placeholder kind="loading" message="Loading execution data…" />
        </Surface>
      ) : (
        <>
          {error && <Note tone="error">{error}</Note>}
          {renderTable({ title: "Buy Orders", data: buyData, orderSide: ORDER_SIDE_BUY, results: buyResults, suggestions: buySuggestions })}
          {renderTable({ title: "Sell Orders", data: sellData, orderSide: ORDER_SIDE_SELL, results: sellResults, suggestions: sellSuggestions })}
        </>
      )}

      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog(EMPTY_CONFIRM)}>
        <DialogTitle>
          {confirmDialog.type === "close" ? "Close Execution?" : "Cancel Execution?"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmDialog.type === "close"
              ? `Are you sure you want to close ${confirmDialog.companyName} — ${confirmDialog.schemeName}? Executed ${formatNumber(confirmDialog.executedQty, 0)} of ${formatNumber(confirmDialog.orderedQty, 0)} qty will be kept.`
              : `Are you sure you want to cancel ${confirmDialog.companyName} — ${confirmDialog.schemeName}? This action cannot be undone.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <KitButton onClick={() => setConfirmDialog(EMPTY_CONFIRM)}>Cancel</KitButton>
          <KitButton
            onClick={executeConfirmedAction}
            variant={confirmDialog.type === "cancel" ? "danger" : "primary"}
            autoFocus
          >
            {confirmDialog.type === "close" ? "Close" : "Confirm"}
          </KitButton>
        </DialogActions>
      </Dialog>

      <Popover
        open={Boolean(orderPopover)}
        anchorEl={orderPopover?.anchor}
        onClose={() => setOrderPopover(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
      >
        {orderPopover && (() => {
          const d = orderPopover.data;
          const isBuy = orderPopover.orderSide === ORDER_SIDE_BUY;
          const fmt = (v: number) => v % 1 === 0 ? String(v) : v.toFixed(2);
          const fmtOrDash = (v: number | null | undefined) => v != null ? fmt(v) : "—";
          const rows: [string, string][] = isBuy && d.cl1 != null ? [
            ["Date", new Date(d.order_date).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })],
            ["Type", d.order_type_display],
            ["Limit Price", formatCurrency(d.limit_price ?? 0)],
            ["CL1/CL2", `${fmtOrDash(d.cl1)}/${fmtOrDash(d.cl2)}`],
            ["Inc To 1/2", `${fmtOrDash(d.increase_to_1)}/${fmtOrDash(d.increase_to_2)}`],
            ["Inc Max 1/2", `${fmtOrDash(d.increment_max_1)}/${fmtOrDash(d.increment_max_2)}`],
            ["Inc By 1/2", `${fmtOrDash(d.increase_by_1)}/${fmtOrDash(d.increase_by_2)}`],
            ["Max Holding %", d.max_holding != null ? `${fmt(d.max_holding)}%` : "—"],
            ["Min Order Value", formatCurrency(d.min_order_val)],
            ["Buy Method", d.method_display],
            ["POV CAP (%)", `${fmt(d.median_volume_pct)}%`],
            ["Apply Vol Cap", d.apply_volume_cap ? "Yes" : "No"],
          ] : [
            ["Date", new Date(d.order_date).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })],
            ["Type", d.order_type_display],
            ["H1/H2", `${fmtOrDash(d.h1)}/${fmtOrDash(d.h2)}`],
            ["Condition 1/2", `${fmtOrDash(d.condition_1)}/${fmtOrDash(d.condition_2)}`],
            ["Min Order Value", formatCurrency(d.min_order_val)],
            ["Sell Method", d.method_display],
            ["POV CAP (%)", `${fmt(d.median_volume_pct)}%`],
            ["Apply Vol Cap", d.apply_volume_cap ? "Yes" : "No"],
          ];
          return (
            <div className={s["popover-content"]}>
              <div className={s["popover-header"]}>Order #{d.id}</div>
              <table className={`${shared.table} ${s["popover-table"]}`}>
                <tbody>
                  {rows.map(([label, value]) => (
                    <tr key={label}>
                      <td className={s["popover-label"]}>{label}</td>
                      <td className={s["popover-value"]}>{value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })()}
      </Popover>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.severity === "error" ? 8000 : 4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </PageFrame>
  );
};

export default OrderExecutionDashboard;
